import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/useToast";
import {
  submitSupportRequest,
  SUPPORT_REQUEST_CATEGORIES,
  type SupportRequestCategory,
} from "@/api/supportRequests";

type SupportRequestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  municipalityId?: number;
  municipalityName?: string;
  source?: string;
  defaultCategory?: SupportRequestCategory;
};

export function SupportRequestDialog({
  open,
  onOpenChange,
  municipalityId,
  source = "launch_pad",
  defaultCategory = "how_to_use",
}: SupportRequestDialogProps) {
  const { toast } = useToast();
  const [category, setCategory] = useState<SupportRequestCategory>(defaultCategory);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setCategory(defaultCategory);
  }, [open, defaultCategory]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="support-request-dialog">
        <DialogHeader>
          <DialogTitle>Contact support</DialogTitle>
          <DialogDescription>
            Tell us what you need. Our team typically responds within one business day.
          </DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = event.currentTarget;
            const formData = new FormData(form);
            const message = (formData.get("message") as string)?.trim();
            if (!message) return;

            setSubmitting(true);
            try {
              await submitSupportRequest({
                message,
                category,
                municipalityId,
                source,
              });
              form.reset();
              onOpenChange(false);
              toast({
                title: "Message sent",
                description: "Thanks — we'll get back to you soon.",
                variant: "success",
              });
            } catch (error) {
              toast({
                title: "Could not send request",
                description: error instanceof Error ? error.message : "Please try again later.",
                variant: "destructive",
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="support-category">Topic</Label>
            <Select
              value={category}
              onValueChange={(value) => setCategory(value as SupportRequestCategory)}
            >
              <SelectTrigger id="support-category" aria-label="Support topic">
                <SelectValue placeholder="Select a topic" />
              </SelectTrigger>
              <SelectContent>
                {SUPPORT_REQUEST_CATEGORIES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="support-message">How can we help?</Label>
            <Textarea
              id="support-message"
              name="message"
              placeholder="Describe your question or issue..."
              className="min-h-[120px] resize-none"
              required
              disabled={submitting}
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send to support"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
